import { CommonModule } from "@angular/common";
import { Component, signal, computed, effect, input, output, inject, ChangeDetectionStrategy, HostListener } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';

import { DataViewModule } from 'primeng/dataview';
import { SelectButtonModule } from 'primeng/selectbutton';
import { FormsModule } from "@angular/forms";
import { PickListModule } from "primeng/picklist";
import { OrderListModule } from "primeng/orderlist";
import { TagModule } from "primeng/tag";
import { ButtonModule } from "primeng/button";
import { SecurePipe } from "src/app/shared/pipes/secure.pipe";
import { DialogHelper } from "src/app/shared/utilities/helpers/dialog-helper/dialog-helper";
import { ToolPreview } from "../../models/tool-preview.model";
import { SkeletonModule } from 'primeng/skeleton';
import { WindowHelper } from "src/app/shared/utilities/helpers/window-helper/window-helper";
import { UserService } from "src/app/shared/services/user.service";
import { ToolService } from "../../services/tool.service";
import { ToolsFormModalComponent } from "../tools-form-modal/tools-form-modal.component";
import { v4 as uuidv4 } from 'uuid';
import { ToolStatus } from '../../models/tool-status.enum';
import { ErrorHandler } from "src/app/shared/utilities/error-handler/error-handler";
import { LoadingOverlayService } from "src/app/shared/services/spinner-overlay.service";
import { finalize } from "rxjs";

@Component({
    selector: 'tools-data-view',
    templateUrl: './tools-data-view.component.html',
    styleUrls: [ './tools-data-view.component.scss' ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, MatProgressSpinnerModule, SecurePipe, DataViewModule, FormsModule, SelectButtonModule, PickListModule, OrderListModule, TagModule, ButtonModule, SkeletonModule, NgbTooltipModule, MatDialogModule]})

export class ToolsDataViewComponent {
    tools = input<ToolPreview[]>([]);
    totalRecords = input<number>(0);
    currentPage = input<number>(1);
    isLoading = input<boolean>(true);
    
    pageChange = output<number>();

    layout = signal<'list' | 'grid'>('list');
    options = ['list', 'grid'];
    rows = 9;
    first = signal<number>(0);
    savedScrollY = signal<number>(0);
    defaultToolsQuantity = 9;
    imageLoaded = signal<boolean[]>(Array(this.defaultToolsQuantity).fill(false));

    onLayoutChange(value: 'list' | 'grid') {
        if (!value) return;
        this.layout.set(value);
        this.updateFormat();
    }
    
    private readonly userService = inject(UserService);
    private readonly dialogHelper = inject(DialogHelper);
    private readonly windowHelper = inject(WindowHelper);
    private readonly toolService = inject(ToolService);
    private readonly dialog = inject(MatDialog);
    private readonly errorHandler = inject(ErrorHandler);
    private readonly loadingOverlayService = inject(LoadingOverlayService);

    isAdmin = this.userService.isAdmin;
    private readonly pendingDraftEditToolId = signal<number | null>(null);
    resizeSignal = toSignal(this.windowHelper.resize$);

    visibleTools = computed(() => {
        const tools = this.tools() ?? [];
        if (this.isAdmin()) return tools;
        return tools.filter(t => t.status !== ToolStatus.Draft);
    });

    constructor() {
        this.userService.fetchAdminStatus();

        effect(() => {
            const pendingToolId = this.pendingDraftEditToolId();
            if (pendingToolId === null) return;

            const isLoadingAdmin = this.userService.isLoading();
            if (isLoadingAdmin) return;

            const isAdmin = this.isAdmin();
            this.pendingDraftEditToolId.set(null);

            if (isAdmin) {
                this.openEditDraftToolDialog(pendingToolId);
                return;
            }

            this.errorHandler.showError('You do not have permission to edit draft tools.');
        });
        
        effect(() => {
            const toolsArray = this.tools();
            if (toolsArray.length > 0) {
                this.imageLoaded.update(current => current.concat(Array(toolsArray.length).fill(false)));
            }
        });

        effect(() => {
            const page = this.currentPage();
            this.first.set((page - 1) * this.rows);
        });

        effect(() => {
            const loading = this.isLoading();
            const scrollY = this.savedScrollY();
            if (!loading && scrollY > 0) {
                setTimeout(() => {
                    window.scrollTo(0, scrollY);
                    this.savedScrollY.set(0);
                }, 50);
            }
        });
        effect(() => {
            this.resizeSignal();
        });
    }

    updateFormat() {
        this.imageLoaded.update(current => current.fill(false));
    }
    
    onImageLoad(i : number): void {
        this.imageLoaded.update(current => {
            const newArray = [...current];
            newArray[i] = true;
            return newArray;
        });
    }

    onImageError(i : number): void {
        this.imageLoaded.update(current => {
            const newArray = [...current];
            newArray[i] = false;
            return newArray;
        });
    }

    openModalDialog(tool: ToolPreview): void {
        const button = document.activeElement as HTMLElement;
        button.blur();

        this.dialogHelper.openToolModalDialog(tool.id);
    }

    openEditToolDialog(toolId: number): void {
        if (!this.isAdmin()) {
            // Admin status may still be loading; queue the click.
            this.pendingDraftEditToolId.set(toolId);
            this.userService.fetchAdminStatus();
            return;
        }

        this.openEditDraftToolDialog(toolId);
    }

    openEditDraftToolDialog(toolId: number): void {
        const uploadSessionKey = uuidv4();

        this.loadingOverlayService.show();
        this.toolService.getToolById(toolId)
            .pipe(finalize(() => this.loadingOverlayService.hide()))
            .subscribe({
                next: (tool) => {
                    if (!tool) {
                        console.error('Tool not found for id', toolId);
                        return;
                    }

                    const dialogRef = this.dialog.open(ToolsFormModalComponent, {
                        width: '50rem',
                        maxWidth: 'calc(100vw - 4rem)',
                        height: 'calc(100vh - 2rem)',
                        maxHeight: 'calc(100vh - 2rem)',
                        autoFocus: true,
                        restoreFocus: true,
                        disableClose: true,
                        
                        data: { tool, uploadSessionKey }
                    });

                    dialogRef.afterClosed().subscribe(result => {
                        if (result?.updated) {
                            this.pageChange.emit(this.currentPage());

                            if (typeof result.updatedToolId === 'number') {
                                setTimeout(() => this.dialogHelper.openToolModalDialog(result.updatedToolId!), 0);
                            }
                        }
                    });
                },
                error: (err) => {
                    console.error('Error loading tool for edit', err);
                    this.errorHandler.showError('Unable to load tool for editing. Please try again.');
                }
            }
        );
    }

    openCreateToolDialog(): void {
        const temporaryBlobId = uuidv4();
        const dialogRef = this.dialog.open(ToolsFormModalComponent, {
            width: '50rem',
            maxWidth: 'calc(100vw - 4rem)',
            height: 'calc(100vh - 2rem)',
            maxHeight: 'calc(100vh - 2rem)',
            autoFocus: true,
            restoreFocus: true,
            disableClose: true,
            data: { uploadSessionKey: temporaryBlobId }
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result?.created) {
                this.pageChange.emit(1);

                if (typeof result.createdToolId === 'number') {
                    // Defer to allow the create dialog to fully close before opening another dialog.
                    setTimeout(() => this.dialogHelper.openToolModalDialog(result.createdToolId!), 0);
                }
            }
        });
    }

    onPageChange(event: { first: number; rows: number }): void {
        this.savedScrollY.set(window.scrollY);

        const newPage = Math.floor(event.first / event.rows) + 1;
        this.pageChange.emit(newPage);
    }

    onDeleteTool(toolId: number): void {
        this.dialogHelper.openConfirmationDialog({
            title: 'Delete Tool',
            message: `Are you sure you want to delete the tool <b>"${this.tools().find(tool => tool.id === toolId)?.name}"</b>? This action cannot be undone.`,
            confirmText: 'Delete',
            cancelText: 'Cancel',
            type: 'delete'
        }).subscribe((confirmed) => {
            if (confirmed) {
                this.toolService.deleteToolById(toolId).subscribe({
                    next: () => {
                        console.log(`Tool with ID ${toolId} deleted successfully.`);
                        this.pageChange.emit(this.currentPage());
                    },
                    error: (error) => {
                        console.error(`Error deleting tool with ID ${toolId}:`, error);
                    }
                });
            }
        });
    }
}